import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { MultiCombobox } from '@components/combobox';
import { Badge } from '@components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card';
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent
} from '@components/ui/chart';
import { cn } from '@core/utils/cn';

import { ActivityChartProps } from '../utils/ActivityChart.types';
import type { SupportedFitnessApp } from '../../../../core/api/FitnessActivityClient';
import { providerLabels } from '../utils/ActivityChart.constants';
import {
    getActivityLabel,
    getActivityColor
} from '../utils/ActivityChart.utils';
import { useActivityChartStore } from '../utils/ActivityChart.store';
import {
    resolveUserOffsetMinutes,
    formatUtcOffsetLabel
} from '../utils/timezoneOffset.utils';

interface ActivityChartComponentProps extends ActivityChartProps {
    isOnlyDay?: boolean;
}

const seriesKey = (index: number) => `s${index}`;

export function ActivityChart({
    data,
    isOnlyDay = false
}: ActivityChartComponentProps) {
    const offsetMinutes = data ? resolveUserOffsetMinutes(data) : 0;
    const utcOffsetLabel = formatUtcOffsetLabel(offsetMinutes);

    const {
        state,
        setSelectedActivities,
        setSelectedProviders,
        chartSeries,
        xAxisData,
        formatXAxisLabel
    } = useActivityChartStore({ data, offsetMinutes });

    const utcMidnight = new Date((data?.userDay ?? '') + 'T00:00:00Z');
    const dayBounds = {
        min: utcMidnight.getTime(),
        max: utcMidnight.getTime() + 24 * 3_600_000 - 1
    };

    const rows = useMemo(
        () =>
            xAxisData.map((time, rowIndex) => {
                const row: Record<string, number> = { time: time.getTime() };
                chartSeries.forEach((series, seriesIndex) => {
                    row[seriesKey(seriesIndex)] = series.data[rowIndex];
                });
                return row;
            }),
        [xAxisData, chartSeries]
    );

    const chartConfig = useMemo<ChartConfig>(
        () =>
            Object.fromEntries(
                chartSeries.map((series, index) => [
                    seriesKey(index),
                    { label: series.label, color: series.color }
                ])
            ),
        [chartSeries]
    );

    if (!data?.dailySteps) {
        return (
            <Card>
                <CardContent className="flex min-h-[400px] items-center justify-center">
                    <p className="text-muted-foreground">
                        No activity data available for this date
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
                <CardTitle>Steps Timeline</CardTitle>
                <div className="flex flex-wrap gap-4">
                    <MultiCombobox
                        className="w-[220px]"
                        placeholder="Activities"
                        options={state.availableActivities}
                        getOptionLabel={getActivityLabel}
                        value={state.selectedActivities}
                        onChange={setSelectedActivities}
                        disabled={state.availableActivities.length === 0}
                        emptyText="No activities available"
                        renderChip={(value, remove) => (
                            <Badge
                                className="gap-1 text-white"
                                style={{
                                    backgroundColor: getActivityColor(value)
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    remove();
                                }}
                            >
                                {getActivityLabel(value)}
                                <X className="size-3" />
                            </Badge>
                        )}
                    />
                    <MultiCombobox
                        className="w-[220px]"
                        placeholder="Providers"
                        options={state.availableProviders}
                        getOptionLabel={(provider: SupportedFitnessApp) =>
                            providerLabels[provider] || provider
                        }
                        value={state.selectedProviders}
                        onChange={setSelectedProviders}
                        disabled={state.availableProviders.length === 0}
                        emptyText="No providers available"
                        renderChip={(provider, remove) => (
                            <Badge
                                variant="info"
                                className="gap-1"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    remove();
                                }}
                            >
                                {providerLabels[provider] || provider}
                                <X className="size-3" />
                            </Badge>
                        )}
                    />
                </div>
            </CardHeader>

            {state.selectedActivities.length === 0 ? null : (
                <CardContent className="relative">
                    <ChartContainer
                        config={chartConfig}
                        className={cn(
                            'w-full',
                            isOnlyDay ? 'aspect-auto h-[320px]' : 'aspect-video'
                        )}
                    >
                        <LineChart
                            data={rows}
                            margin={{
                                left: 12,
                                right: 20,
                                top: 20,
                                bottom: 10
                            }}
                        >
                            <CartesianGrid vertical={false} />
                            <XAxis
                                dataKey="time"
                                type="number"
                                scale="time"
                                domain={[dayBounds.min, dayBounds.max]}
                                tickFormatter={(value: number) =>
                                    formatXAxisLabel(new Date(value))
                                }
                            />
                            <YAxis
                                width={70}
                                tickFormatter={(value: number) =>
                                    value.toLocaleString()
                                }
                            />
                            <ChartTooltip
                                content={
                                    <ChartTooltipContent
                                        labelFormatter={(_, payload) =>
                                            formatXAxisLabel(
                                                new Date(
                                                    payload?.[0]?.payload?.time
                                                )
                                            )
                                        }
                                    />
                                }
                            />
                            {chartSeries.map((series, index) => (
                                <Line
                                    key={seriesKey(index)}
                                    dataKey={seriesKey(index)}
                                    type="stepAfter"
                                    stroke={series.color}
                                    strokeWidth={2}
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            ))}
                        </LineChart>
                    </ChartContainer>
                    <span className="pointer-events-none absolute bottom-1 left-6 text-[11px] leading-none text-muted-foreground/60">
                        {utcOffsetLabel}
                    </span>
                </CardContent>
            )}
        </Card>
    );
}
